import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Row,
  Col,
  Select,
  Spin,
  Typography,
  Tooltip,
  Space,
  Button,
  Empty,
  Badge,
  Alert,
} from "antd";
import {
  GlobalOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  SecurityScanOutlined,
  HeatMapOutlined,
  FunnelPlotOutlined,
  LaptopOutlined,
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
  FireOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import {
  getVisitorAnalyticsOverview,
  getVisitorUrlAnalytics,
  getVisitorGeographicAnalytics,
  getVisitorTrafficSourceAnalytics,
  getVisitorRealTimeActivity,
  getVisitorTemporalAnalytics,
  getVisitorSecurityAnalytics,
  getVisitorAdvancedGeographicAnalytics,
  getVisitorCampaignAnalytics,
  getVisitorDeviceAnalytics,
  getVisitorEngagementMetrics,
} from "@api/user/superAdmin";

const { Text } = Typography;
const { Option } = Select;

// Country flags mapping
const countryFlags = {
  'US': '🇺🇸', 'GB': '🇬🇧', 'UK': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺', 'DE': '🇩🇪', 'FR': '🇫🇷',
  'IN': '🇮🇳', 'JP': '🇯🇵', 'CN': '🇨🇳', 'BR': '🇧🇷', 'MX': '🇲🇽', 'ES': '🇪🇸', 'IT': '🇮🇹',
  'NL': '🇳🇱', 'SE': '🇸🇪', 'NO': '🇳🇴', 'SG': '🇸🇬', 'AE': '🇦🇪', 'RU': '🇷🇺', 'KR': '🇰🇷',
  'PK': '🇵🇰', 'BD': '🇧🇩', 'PH': '🇵🇭', 'ID': '🇮🇩', 'TH': '🇹🇭', 'VN': '🇻🇳', 'MY': '🇲🇾',
  'ZA': '🇿🇦', 'NG': '🇳🇬', 'EG': '🇪🇬', 'TR': '🇹🇷', 'PL': '🇵🇱', 'UA': '🇺🇦', 'NZ': '🇳🇿',
  'IE': '🇮🇪', 'CH': '🇨🇭', 'AT': '🇦🇹', 'BE': '🇧🇪', 'PT': '🇵🇹', 'GR': '🇬🇷', 'IL': '🇮🇱',
  'SA': '🇸🇦', 'HK': '🇭🇰', 'TW': '🇹🇼', 'AR': '🇦🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'CZ': '🇨🇿',
};

const getFlag = (code) => countryFlags[code?.toUpperCase()] || '🌍';

// Card wrapper component
const ChartCard = ({ title, subtitle, badge, children, minHeight = 300 }) => (
  <div style={{
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
  }}>
    <div style={{
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}>
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
      {badge !== undefined && (
        <Badge count={badge} style={{ backgroundColor: "#8b5cf6" }} overflowCount={999} />
      )}
    </div>
    <div style={{ minHeight }}>{children}</div>
  </div>
);

// Metric card component
const MetricCard = ({ title, value, icon, color }) => {
  const colorMap = {
    blue: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", text: "#1d4ed8", gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" },
    green: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", text: "#059669", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
    purple: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", text: "#7c3aed", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
    orange: { bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", text: "#d97706", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
    purple: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", text: "#7c3aed", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
    pink: { bg: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)", text: "#db2777", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
    red: { bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)", text: "#dc2626", gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div style={{
      background: c.bg,
      borderRadius: "16px",
      padding: "20px",
      border: "1px solid rgba(0,0,0,0.06)",
      minHeight: "120px",
    }}>
      <div style={{
        width: "44px",
        height: "44px",
        borderRadius: "12px",
        background: c.gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: "18px",
        marginBottom: "14px",
      }}>
        {icon}
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: c.text, lineHeight: 1.1 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize: "13px", fontWeight: 500, color: "#64748b", marginTop: "4px" }}>
        {title}
      </div>
    </div>
  );
};

const VisitorAnalytics = ({ showHeader = true }) => {
  const history = useHistory();
  const location = useLocation();

  const getInitialDateRange = () => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const validDateRanges = [7, 30, 90, 365, 730, 9999];
    const parsedDate = parseInt(dateParam);
    return validDateRanges.includes(parsedDate) ? parsedDate : 7;
  };

  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");
    const validTabs = ["overview", "engagement", "temporal", "security", "geographic", "campaigns", "devices"];
    return validTabs.includes(tabParam) ? tabParam : "overview";
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Tab configuration
  const tabs = [
    { key: "overview", label: "Overview", icon: <TrophyOutlined /> },
    { key: "engagement", label: "Engagement", icon: <BarChartOutlined /> },
    { key: "temporal", label: "Time", icon: <ClockCircleOutlined /> },
    { key: "security", label: "Security", icon: <SecurityScanOutlined /> },
    { key: "geographic", label: "Geographic", icon: <HeatMapOutlined /> },
    { key: "campaigns", label: "Campaigns", icon: <FunnelPlotOutlined /> },
    { key: "devices", label: "Devices", icon: <LaptopOutlined /> },
  ];

  // React Query hooks
  const { data: overview, isLoading: overviewLoading, refetch } = useQuery(
    ["visitorOverview", dateRange],
    async () => {
      const res = await getVisitorAnalyticsOverview(dateRange);
      return res.data.data;
    },
    { staleTime: 60000, keepPreviousData: true }
  );

  const { data: urlStats = [] } = useQuery(
    ["visitorUrlStats", dateRange],
    async () => {
      const res = await getVisitorUrlAnalytics(dateRange);
      return res.data.data || [];
    },
    { enabled: activeTab === "overview", staleTime: 60000, keepPreviousData: true }
  );

  const { data: sourceStats = [] } = useQuery(
    ["visitorSourceStats", dateRange],
    async () => {
      const res = await getVisitorTrafficSourceAnalytics(dateRange);
      return res.data.data || [];
    },
    { enabled: activeTab === "overview" || activeTab === "campaigns", staleTime: 60000, keepPreviousData: true }
  );

  const { data: realtimeData } = useQuery(
    ["visitorRealtime"],
    async () => {
      const res = await getVisitorRealTimeActivity(24);
      return res.data.data;
    },
    { enabled: activeTab === "overview", staleTime: 20000, refetchInterval: 30000 }
  );

  const { data: temporalData } = useQuery(
    ["visitorTemporal", dateRange],
    async () => {
      const res = await getVisitorTemporalAnalytics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "overview" || activeTab === "temporal", staleTime: 60000, keepPreviousData: true }
  );

  const { data: securityData } = useQuery(
    ["visitorSecurity", dateRange],
    async () => {
      const res = await getVisitorSecurityAnalytics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "security", staleTime: 60000, keepPreviousData: true }
  );

  const { data: advancedGeoData } = useQuery(
    ["visitorAdvancedGeo", dateRange],
    async () => {
      const res = await getVisitorAdvancedGeographicAnalytics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "geographic", staleTime: 60000, keepPreviousData: true }
  );

  const { data: campaignData } = useQuery(
    ["visitorCampaign", dateRange],
    async () => {
      const res = await getVisitorCampaignAnalytics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "campaigns", staleTime: 60000, keepPreviousData: true }
  );

  const { data: deviceData } = useQuery(
    ["visitorDevice", dateRange],
    async () => {
      const res = await getVisitorDeviceAnalytics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "devices", staleTime: 60000, keepPreviousData: true }
  );

  const { data: engagementData } = useQuery(
    ["visitorEngagement", dateRange],
    async () => {
      const res = await getVisitorEngagementMetrics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "engagement", staleTime: 60000, keepPreviousData: true }
  );

  const { data: geoData } = useQuery(
    ["visitorGeo", dateRange],
    async () => {
      const res = await getVisitorGeographicAnalytics(dateRange);
      return res.data.data;
    },
    { enabled: activeTab === "geographic", staleTime: 60000, keepPreviousData: true }
  );

  const loading = overviewLoading;

  const updateURL = (newDateRange, newTab) => {
    const params = new URLSearchParams(location.search);
    params.set("dateRange", newDateRange.toString());
    params.set("tab", newTab);
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    updateURL(newDateRange, activeTab);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    updateURL(dateRange, newTab);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const tabParam = urlParams.get("tab");

    if (dateParam) {
      const validDateRanges = [7, 30, 90, 365, 730, 9999];
      const parsedDate = parseInt(dateParam);
      if (validDateRanges.includes(parsedDate) && parsedDate !== dateRange) {
        setDateRange(parsedDate);
      }
    }

    if (tabParam) {
      const validTabs = ["overview", "engagement", "temporal", "security", "geographic", "campaigns", "devices"];
      if (validTabs.includes(tabParam) && tabParam !== activeTab) {
        setActiveTab(tabParam);
      }
    }
  }, [location.search]);

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  };

  const formatSource = (source) => {
    if (!source || source === "naked") return "Direct";
    try {
      const urlObj = new URL(source);
      return urlObj.hostname.replace("www.", "");
    } catch {
      return source.length > 20 ? source.substring(0, 20) + "..." : source;
    }
  };

  if (loading && !overview) {
    return (
      <div style={{
        background: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px"
      }}>
        <Spin size="large" />
        <Text style={{ color: "#64748b" }}>Loading visitor analytics...</Text>
      </div>
    );
  }

  // Process temporal data
  const weeklyData = temporalData?.weekly || [];
  const maxWeeklyVisits = Math.max(...weeklyData.map(d => d.visits || 0), 1);
  const totalWeeklyVisits = weeklyData.reduce((sum, d) => sum + (d.visits || 0), 0);
  const bestDay = weeklyData.reduce((best, d) => (d.visits > (best?.visits || 0) ? d : best), {});
  const hourlyData = temporalData?.hourly || [];
  const maxHourlyVisits = Math.max(...hourlyData.map(h => h.visits || 0), 1);
  const dailyData = temporalData?.daily || [];

  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

  return (
    <div style={{ background: "#f8fafc", minHeight: showHeader ? "calc(100vh - 80px)" : "auto" }}>
      {/* Hero Header */}
      {showHeader && (
        <div
          style={{
            background: "linear-gradient(135deg, #0f766e 0%, #8b5cf6 50%, #2dd4bf 100%)",
            padding: "32px 24px",
            marginBottom: "24px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }} />

          <Row align="middle" justify="space-between" style={{ position: "relative", zIndex: 1, maxWidth: "1400px", margin: "0 auto" }}>
            <Col xs={24} lg={16}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "14px",
                  background: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <GlobalOutlined style={{ fontSize: "24px", color: "#fff" }} />
                </div>
                <h1 style={{ fontSize: "28px", fontWeight: 700, color: "#fff", margin: 0 }}>
                  Visitor Analytics
                </h1>
              </div>
              <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>
                Track global visitor behavior, traffic sources, and engagement
              </p>
            </Col>
            <Col xs={24} lg={8} style={{ textAlign: "right", marginTop: window.innerWidth < 992 ? "20px" : 0 }}>
              <Space size="middle">
                <Select value={dateRange} onChange={handleDateRangeChange} style={{ width: 150 }}>
                  <Option value={7}>Last 7 days</Option>
                  <Option value={30}>Last 30 days</Option>
                  <Option value={90}>Last 90 days</Option>
                  <Option value={365}>Last year</Option>
                  <Option value={730}>Last 2 years</Option>
                  <Option value={9999}>All time</Option>
                </Select>
                <Button
                  icon={<ReloadOutlined spin={loading} />}
                  onClick={() => refetch()}
                  style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff" }}
                />
              </Space>
            </Col>
          </Row>
        </div>
      )}

      {/* Content */}
      <div style={{ padding: showHeader ? "0 24px 24px" : "24px", maxWidth: "1448px", margin: "0 auto" }}>
        {/* Controls when header hidden */}
        {!showHeader && (
          <Row justify="end" style={{ marginBottom: "16px" }}>
            <Space size="middle">
              <Select value={dateRange} onChange={handleDateRangeChange} style={{ width: 150 }}>
                <Option value={7}>Last 7 days</Option>
                <Option value={30}>Last 30 days</Option>
                <Option value={90}>Last 90 days</Option>
                <Option value={365}>Last year</Option>
                <Option value={730}>Last 2 years</Option>
                <Option value={9999}>All time</Option>
              </Select>
              <Button icon={<ReloadOutlined spin={loading} />} onClick={() => refetch()} />
            </Space>
          </Row>
        )}

        {/* Metric Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Total Visits" value={overview?.totalVisits || 0} icon={<EyeOutlined />} color="blue" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Unique IPs" value={overview?.uniqueIPs || 0} icon={<UserOutlined />} color="green" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Countries" value={overview?.uniqueCountries || 0} icon={<GlobalOutlined />} color="purple" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Live (24h)" value={realtimeData?.recentVisitors?.length || 0} icon={<ThunderboltOutlined />} color="orange" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Top Pages" value={urlStats?.length || 0} icon={<LinkOutlined />} color="pink" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Sources" value={sourceStats?.length || 0} icon={<TeamOutlined />} color="purple" />
          </Col>
        </Row>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                padding: "10px 20px",
                fontSize: "13px",
                fontWeight: activeTab === tab.key ? 600 : 500,
                color: activeTab === tab.key ? "#fff" : "#64748b",
                background: activeTab === tab.key
                  ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                  : "#fff",
                border: activeTab === tab.key ? "none" : "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: activeTab === tab.key ? "0 4px 12px rgba(20, 184, 166, 0.3)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <>
            {/* Charts Row 1 */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={8}>
                <ChartCard title="Top Countries" subtitle="Visitors by location" badge={overview?.topCountries?.length} minHeight={320}>
                  {overview?.topCountries?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {overview.topCountries.slice(0, 8).map((country, index) => {
                        const maxVisits = Math.max(...overview.topCountries.map(c => c.visits), 1);
                        const widthPercent = (country.visits / maxVisits) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={country.country} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "10px 12px",
                            background: index === 0 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index === 0 ? "1px solid #99f6e4" : "1px solid transparent",
                            borderRadius: "10px",
                          }}>
                            <span style={{ fontSize: "20px" }}>{getFlag(country.countryCode)}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                <Text style={{ fontWeight: index === 0 ? 700 : 500, fontSize: "13px" }}>
                                  {index === 0 && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 4 }} />}
                                  {country.country}
                                </Text>
                                <Text strong style={{ color }}>{country.visits.toLocaleString()}</Text>
                              </div>
                              <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                                <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "3px" }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={8}>
                <ChartCard title="Weekly Pattern" subtitle="Activity by day" minHeight={320}>
                  {weeklyData.length > 0 ? (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {weeklyData.map((day) => {
                          const widthPercent = (day.visits / maxWeeklyVisits) * 100;
                          const isBest = day.dayName === bestDay.dayName && day.visits > 0;
                          const color = isBest ? "#10b981" : (day.dayName === "Saturday" || day.dayName === "Sunday" ? "#f59e0b" : "#3b82f6");
                          return (
                            <div key={day.dayName} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{ width: "36px", fontSize: "12px", fontWeight: isBest ? 700 : 500, color }}>{day.dayName?.substring(0, 3)}</div>
                              <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                                <div style={{ width: `${Math.max(widthPercent, 3)}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: widthPercent > 20 ? "8px" : 0 }}>
                                  {widthPercent > 20 && <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>{day.visits.toLocaleString()}</span>}
                                </div>
                                {widthPercent <= 20 && <span style={{ position: "absolute", left: `calc(${Math.max(widthPercent, 3)}% + 6px)`, top: "50%", transform: "translateY(-50%)", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>{day.visits.toLocaleString()}</span>}
                              </div>
                              <div style={{ width: "40px", textAlign: "right", fontSize: "11px", color: "#94a3b8" }}>{totalWeeklyVisits > 0 ? ((day.visits / totalWeeklyVisits) * 100).toFixed(0) : 0}%</div>
                            </div>
                          );
                        })}
                      </div>
                      {bestDay.dayName && (
                        <div style={{ marginTop: "16px", padding: "12px 14px", background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", borderRadius: "10px", border: "1px solid #a7f3d0", display: "flex", justifyContent: "space-between" }}>
                          <div><div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Best Day</div><div style={{ fontSize: "14px", fontWeight: 700, color: "#047857" }}>{bestDay.dayName}</div></div>
                          <div style={{ textAlign: "right" }}><div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Visits</div><div style={{ fontSize: "14px", fontWeight: 700, color: "#047857" }}>{bestDay.visits?.toLocaleString()}</div></div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={8}>
                <ChartCard title="Hourly Activity" subtitle="Peak hours" minHeight={320}>
                  {hourlyData.length > 0 ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: "4px" }}>
                        {Array.from({ length: 24 }, (_, hour) => {
                          const hData = hourlyData.find(h => h.hour === hour);
                          const visits = hData?.visits || 0;
                          const intensity = visits / maxHourlyVisits;
                          const bgColor = visits === 0 ? "#f1f5f9" : intensity < 0.25 ? "#dbeafe" : intensity < 0.5 ? "#93c5fd" : intensity < 0.75 ? "#3b82f6" : "#1d4ed8";
                          return (
                            <Tooltip key={hour} title={`${hour}:00 - ${visits.toLocaleString()} visits`}>
                              <div style={{ height: "36px", background: bgColor, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 600, color: intensity > 0.5 ? "#fff" : "#64748b", cursor: "pointer" }}>{hour}</div>
                            </Tooltip>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "16px", justifyContent: "center" }}>
                        <span style={{ fontSize: "11px", color: "#94a3b8" }}>Less</span>
                        {["#f1f5f9", "#dbeafe", "#93c5fd", "#3b82f6", "#1d4ed8"].map((color, i) => (
                          <div key={i} style={{ width: "20px", height: "14px", background: color, borderRadius: "3px" }} />
                        ))}
                        <span style={{ fontSize: "11px", color: "#94a3b8" }}>More</span>
                      </div>
                      <div style={{ marginTop: "12px", padding: "10px 14px", background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", borderRadius: "8px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                        <ClockCircleOutlined style={{ color: "#3b82f6" }} />
                        <span style={{ fontSize: "12px", color: "#64748b" }}>Peak:</span>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#1d4ed8" }}>{hourlyData.reduce((max, h) => h.visits > (max?.visits || 0) ? h : max, {}).hour}:00</span>
                      </div>
                    </>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Charts Row 2 */}
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Top Pages" subtitle="Most visited" badge={urlStats?.length} minHeight={280}>
                  {urlStats?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {urlStats.slice(0, 6).map((page, index) => {
                        const maxVisits = Math.max(...urlStats.map(p => p.visits), 1);
                        const widthPercent = (page.visits / maxVisits) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={page.url} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", background: index === 0 ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" : "#fff", border: index === 0 ? "1px solid #93c5fd" : "1px solid #e2e8f0", borderRadius: "10px" }}>
                            <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "11px", fontWeight: 700 }}>{index + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={page.url}><Text ellipsis style={{ fontSize: "13px", fontWeight: 500, display: "block" }}>{formatUrl(page.url)}</Text></Tooltip>
                              <div style={{ height: "4px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden", marginTop: "6px" }}><div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "2px" }} /></div>
                            </div>
                            <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, color, fontSize: "14px" }}>{page.visits.toLocaleString()}</div><div style={{ fontSize: "10px", color: "#94a3b8" }}>{page.uniqueCountries} countries</div></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={12}>
                <ChartCard title="Traffic Sources" subtitle="Where visitors come from" badge={sourceStats?.length} minHeight={280}>
                  {sourceStats?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {sourceStats.slice(0, 6).map((source, index) => {
                        const color = colors[index % colors.length];
                        const total = sourceStats.reduce((sum, s) => sum + s.visits, 0);
                        const percent = total > 0 ? ((source.visits / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={source.source || "direct"} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", background: index === 0 ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" : "#fff", border: index === 0 ? "1px solid #6ee7b7" : "1px solid #e2e8f0", borderRadius: "10px" }}>
                            <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "11px", fontWeight: 700 }}>{index + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text ellipsis style={{ fontSize: "13px", fontWeight: 500, display: "block" }}>{formatSource(source.source)}</Text>
                              <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>{percent}% of traffic</div>
                            </div>
                            <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, color, fontSize: "14px" }}>{source.visits.toLocaleString()}</div><div style={{ fontSize: "10px", color: "#94a3b8" }}>{source.uniqueCountries} countries</div></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Continent Breakdown */}
            {overview?.continentBreakdown?.length > 0 && (
              <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                <Col xs={24}>
                  <ChartCard title="Global Reach" subtitle="Visitors by continent" minHeight={160}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
                      {overview.continentBreakdown.map((continent, index) => {
                        const continentEmoji = {
                          "North America": "🌎",
                          "South America": "🌎",
                          "Europe": "🌍",
                          "Africa": "🌍",
                          "Asia": "🌏",
                          "Oceania": "🌏",
                          "Other": "🌐"
                        };
                        const continentColors = {
                          "North America": { bg: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)", border: "#93c5fd", text: "#1d4ed8" },
                          "South America": { bg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", border: "#fcd34d", text: "#b45309" },
                          "Europe": { bg: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)", border: "#c4b5fd", text: "#7c3aed" },
                          "Africa": { bg: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)", border: "#fca5a5", text: "#dc2626" },
                          "Asia": { bg: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)", border: "#6ee7b7", text: "#047857" },
                          "Oceania": { bg: "linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)", border: "#67e8f9", text: "#0891b2" },
                          "Other": { bg: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", border: "#cbd5e1", text: "#475569" },
                        };
                        const colors = continentColors[continent.continent] || continentColors.Other;
                        const total = overview.continentBreakdown.reduce((sum, c) => sum + c.visits, 0);
                        const percent = total > 0 ? ((continent.visits / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={continent.continent} style={{
                            padding: "16px 24px",
                            background: colors.bg,
                            border: `1px solid ${colors.border}`,
                            borderRadius: "14px",
                            textAlign: "center",
                            minWidth: "140px",
                            flex: "1 1 auto",
                            maxWidth: "200px"
                          }}>
                            <div style={{ fontSize: "28px", marginBottom: "6px" }}>{continentEmoji[continent.continent] || "🌐"}</div>
                            <div style={{ fontSize: "11px", color: colors.text, fontWeight: 500, marginBottom: "4px" }}>{continent.continent}</div>
                            <div style={{ fontSize: "24px", fontWeight: 700, color: colors.text }}>{continent.visits.toLocaleString()}</div>
                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                              {percent}% • {continent.countries} countries
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ChartCard>
                </Col>
              </Row>
            )}
          </>
        )}

        {activeTab === "engagement" && (
          <>
            {/* Engagement Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #a7f3d0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <UserOutlined style={{ color: "#059669" }} />
                    <span style={{ fontSize: "12px", color: "#059669", fontWeight: 500 }}>New Visitors</span>
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#047857" }}>
                    {engagementData?.newVsReturning?.new?.count?.toLocaleString() || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#10b981", marginTop: "4px" }}>
                    {engagementData?.newVsReturning?.new?.percentage || 0}% of total
                  </div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #93c5fd",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <FireOutlined style={{ color: "#1d4ed8" }} />
                    <span style={{ fontSize: "12px", color: "#1d4ed8", fontWeight: 500 }}>Returning</span>
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#1d4ed8" }}>
                    {engagementData?.newVsReturning?.returning?.count?.toLocaleString() || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#3b82f6", marginTop: "4px" }}>
                    {engagementData?.newVsReturning?.returning?.percentage || 0}% of total
                  </div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #e879f9",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <BarChartOutlined style={{ color: "#a855f7" }} />
                    <span style={{ fontSize: "12px", color: "#a855f7", fontWeight: 500 }}>Avg Visits/IP</span>
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#7c3aed" }}>
                    {engagementData?.summary?.avgVisitsPerIP || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#a855f7", marginTop: "4px" }}>
                    visits per unique visitor
                  </div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: engagementData?.summary?.visitGrowthRate >= 0
                    ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                    : "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: engagementData?.summary?.visitGrowthRate >= 0 ? "1px solid #a7f3d0" : "1px solid #fecaca",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    {engagementData?.summary?.visitGrowthRate >= 0
                      ? <RiseOutlined style={{ color: "#059669" }} />
                      : <FallOutlined style={{ color: "#dc2626" }} />
                    }
                    <span style={{ fontSize: "12px", color: engagementData?.summary?.visitGrowthRate >= 0 ? "#059669" : "#dc2626", fontWeight: 500 }}>Growth Rate</span>
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: engagementData?.summary?.visitGrowthRate >= 0 ? "#047857" : "#dc2626" }}>
                    {engagementData?.summary?.visitGrowthRate >= 0 ? "+" : ""}{engagementData?.summary?.visitGrowthRate || 0}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                    vs previous period
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              {/* New vs Returning Donut */}
              <Col xs={24} lg={8}>
                <ChartCard title="New vs Returning" subtitle="Visitor segmentation" minHeight={300}>
                  {engagementData?.newVsReturning ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
                      {/* Visual Donut Chart */}
                      <div style={{ position: "relative", width: "180px", height: "180px" }}>
                        <svg viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)", width: "100%", height: "100%" }}>
                          <circle cx="18" cy="18" r="15.915" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                          <circle
                            cx="18" cy="18" r="15.915" fill="none"
                            stroke="#10b981" strokeWidth="3"
                            strokeDasharray={`${engagementData.newVsReturning.new?.percentage || 0} ${100 - (engagementData.newVsReturning.new?.percentage || 0)}`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                          <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>
                            {(engagementData.summary?.uniqueIPs || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: "11px", color: "#64748b" }}>Total IPs</div>
                        </div>
                      </div>
                      {/* Legend */}
                      <div style={{ display: "flex", gap: "24px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: "#10b981" }} />
                          <div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>New</div>
                            <div style={{ fontSize: "11px", color: "#64748b" }}>{engagementData.newVsReturning.new?.percentage}%</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: "#3b82f6" }} />
                          <div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>Returning</div>
                            <div style={{ fontSize: "11px", color: "#64748b" }}>{engagementData.newVsReturning.returning?.percentage}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              {/* Frequency Distribution */}
              <Col xs={24} lg={8}>
                <ChartCard title="Visit Frequency" subtitle="How often visitors return" minHeight={300}>
                  {engagementData?.frequencyDistribution?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {engagementData.frequencyDistribution.map((freq, index) => {
                        const maxIPs = Math.max(...engagementData.frequencyDistribution.map(f => f.uniqueIPs), 1);
                        const widthPercent = (freq.uniqueIPs / maxIPs) * 100;
                        const colors = ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899", "#06b6d4"];
                        const color = colors[index % colors.length];
                        return (
                          <div key={freq.frequency} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "80px", fontSize: "12px", fontWeight: 500, color: "#64748b" }}>{freq.frequency}</div>
                            <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                              <div style={{
                                width: `${Math.max(widthPercent, 5)}%`,
                                height: "100%",
                                background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                paddingRight: widthPercent > 25 ? "8px" : 0
                              }}>
                                {widthPercent > 25 && <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>{freq.uniqueIPs}</span>}
                              </div>
                              {widthPercent <= 25 && <span style={{ position: "absolute", left: `calc(${Math.max(widthPercent, 5)}% + 6px)`, top: "50%", transform: "translateY(-50%)", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>{freq.uniqueIPs}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              {/* Peak Activity */}
              <Col xs={24} lg={8}>
                <ChartCard title="Peak Activity" subtitle="When visitors are most active" minHeight={300}>
                  {engagementData?.peakActivityTimes ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {/* Peak Hour */}
                      <div style={{ padding: "16px", background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", borderRadius: "12px", border: "1px solid #fcd34d" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: "11px", color: "#b45309", fontWeight: 500, marginBottom: "4px" }}>PEAK HOUR</div>
                            <div style={{ fontSize: "24px", fontWeight: 700, color: "#92400e" }}>{engagementData.peakActivityTimes.peakHour?.hour || 0}:00</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "18px", fontWeight: 700, color: "#d97706" }}>{engagementData.peakActivityTimes.peakHour?.visits?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: "11px", color: "#b45309" }}>visits</div>
                          </div>
                        </div>
                      </div>
                      {/* Peak Day */}
                      <div style={{ padding: "16px", background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)", borderRadius: "12px", border: "1px solid #93c5fd" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: "11px", color: "#1d4ed8", fontWeight: 500, marginBottom: "4px" }}>PEAK DAY</div>
                            <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e40af" }}>{engagementData.peakActivityTimes.peakDay?.dayName || "—"}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "18px", fontWeight: 700, color: "#2563eb" }}>{engagementData.peakActivityTimes.peakDay?.visits?.toLocaleString() || 0}</div>
                            <div style={{ fontSize: "11px", color: "#1d4ed8" }}>visits</div>
                          </div>
                        </div>
                      </div>
                      {/* Growth Comparison */}
                      <div style={{ padding: "16px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 500, marginBottom: "8px" }}>PERIOD COMPARISON</div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>Current</div>
                            <div style={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>{engagementData.growthTrends?.comparison?.currentPeriod?.visits?.toLocaleString() || 0}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>Previous</div>
                            <div style={{ fontSize: "16px", fontWeight: 700, color: "#64748b" }}>{engagementData.growthTrends?.comparison?.previousPeriod?.visits?.toLocaleString() || 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Monthly Growth & Top Engaged */}
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <ChartCard title="Monthly Growth Trend" subtitle="Visits over time" minHeight={350}>
                  {engagementData?.growthTrends?.monthly?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {engagementData.growthTrends.monthly.slice(-12).map((month, index) => {
                        const maxVisits = Math.max(...engagementData.growthTrends.monthly.slice(-12).map(m => m.visits), 1);
                        const widthPercent = (month.visits / maxVisits) * 100;
                        return (
                          <div key={`${month.year}-${month.month}`} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "90px", fontSize: "12px", color: "#64748b" }}>{month.monthName?.substring(0, 3)} {month.year}</div>
                            <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                              <div style={{
                                width: `${widthPercent}%`,
                                height: "100%",
                                background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                paddingRight: widthPercent > 20 ? "8px" : 0
                              }}>
                                {widthPercent > 20 && <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>{month.visits.toLocaleString()}</span>}
                              </div>
                              {widthPercent <= 20 && <span style={{ position: "absolute", left: `calc(${widthPercent}% + 6px)`, top: "50%", transform: "translateY(-50%)", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>{month.visits.toLocaleString()}</span>}
                            </div>
                            <div style={{ width: "50px", textAlign: "right", fontSize: "11px", color: "#94a3b8" }}>{month.uniqueCountries} 🌍</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={10}>
                <ChartCard title="Top Engaged Visitors" subtitle="Most loyal IPs" badge={engagementData?.topEngagedVisitors?.length} minHeight={350}>
                  {engagementData?.topEngagedVisitors?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {engagementData.topEngagedVisitors.slice(0, 8).map((visitor, index) => (
                        <div key={visitor.ip} style={{
                          padding: "12px 14px",
                          background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                          border: index < 3 ? "1px solid #99f6e4" : "1px solid #e2e8f0",
                          borderRadius: "10px"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                {index < 3 && <TrophyOutlined style={{ color: index === 0 ? "#f59e0b" : index === 1 ? "#94a3b8" : "#cd7f32", fontSize: "12px" }} />}
                                <code style={{ fontSize: "12px", color: "#64748b" }}>{visitor.ip}</code>
                              </div>
                              <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                                {visitor.uniquePages} pages • {visitor.engagementSpan}d span
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "18px", fontWeight: 700, color: "#7c3aed" }}>{visitor.visits}</div>
                              <div style={{ fontSize: "10px", color: "#64748b" }}>visits</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "temporal" && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <ChartCard title="Daily Traffic" subtitle="Last 14 days" minHeight={400}>
                {dailyData.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {dailyData.slice(-14).map((day, index) => {
                      const maxVisits = Math.max(...dailyData.slice(-14).map(d => d.visits), 1);
                      const widthPercent = (day.visits / maxVisits) * 100;
                      return (
                        <div key={day.date} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div style={{ width: "80px", fontSize: "12px", color: "#64748b" }}>{new Date(day.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                          <div style={{ flex: 1, height: "20px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                            <div style={{ width: `${widthPercent}%`, height: "100%", background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)", borderRadius: "4px" }} />
                          </div>
                          <div style={{ width: "60px", textAlign: "right", fontSize: "12px", fontWeight: 600, color: "#7c3aed" }}>{day.visits.toLocaleString()}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                )}
              </ChartCard>
            </Col>
            <Col xs={24} lg={12}>
              <ChartCard title="Weekly Distribution" subtitle="Activity by day of week" minHeight={400}>
                {weeklyData.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {weeklyData.map((day) => {
                      const widthPercent = (day.visits / maxWeeklyVisits) * 100;
                      const isBest = day.dayName === bestDay.dayName;
                      return (
                        <div key={day.dayName} style={{ padding: "16px", background: isBest ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" : "#f8fafc", borderRadius: "12px", border: isBest ? "1px solid #6ee7b7" : "1px solid transparent" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                            <Text style={{ fontWeight: 600, color: isBest ? "#047857" : "#1e293b" }}>{isBest && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 6 }} />}{day.dayName}</Text>
                            <Text strong style={{ color: isBest ? "#047857" : "#64748b" }}>{day.visits.toLocaleString()} visits</Text>
                          </div>
                          <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                            <div style={{ width: `${widthPercent}%`, height: "100%", background: isBest ? "linear-gradient(90deg, #10b981 0%, #059669 100%)" : "linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)", borderRadius: "4px" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                )}
              </ChartCard>
            </Col>
          </Row>
        )}

        {activeTab === "security" && (
          <>
            {/* Security Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #99f6e4",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#7c3aed" }}>{securityData?.summary?.totalUniqueIPs?.toLocaleString() || 0}</div>
                  <div style={{ color: "#8b5cf6", fontSize: "12px", fontWeight: 500, marginTop: 4 }}>Unique IPs</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #93c5fd",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#1d4ed8" }}>{securityData?.summary?.repeatVisitorCount?.toLocaleString() || 0}</div>
                  <div style={{ color: "#3b82f6", fontSize: "12px", fontWeight: 500, marginTop: 4 }}>Repeat Visitors</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #fca5a5",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#dc2626" }}>{securityData?.summary?.suspiciousIPs?.toLocaleString() || 0}</div>
                  <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 500, marginTop: 4 }}>Flagged IPs</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #fcd34d",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#d97706" }}>{securityData?.summary?.multiCountryIPs?.toLocaleString() || 0}</div>
                  <div style={{ color: "#f59e0b", fontSize: "12px", fontWeight: 500, marginTop: 4 }}>Multi-Country</div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={12}>
                <ChartCard title="Top IP Addresses" subtitle="Most active with risk indicators" badge={securityData?.topIPs?.length} minHeight={380}>
                  {securityData?.topIPs?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {securityData.topIPs.slice(0, 8).map((ip, index) => (
                        <div key={ip.ip} style={{
                          padding: "12px 14px",
                          borderRadius: "10px",
                          backgroundColor: ip.suspiciousScore > 4 ? "#fef2f2" : ip.suspiciousScore > 0 ? "#fffbeb" : "#f0fdf4",
                          border: `1px solid ${ip.suspiciousScore > 4 ? "#fecaca" : ip.suspiciousScore > 0 ? "#fde68a" : "#bbf7d0"}`
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <code style={{ fontWeight: 600, fontSize: "13px" }}>{ip.ip}</code>
                                {ip.suspiciousScore > 4 && <span style={{ padding: "2px 6px", background: "#fecaca", color: "#dc2626", borderRadius: "4px", fontSize: "9px", fontWeight: 600 }}>HIGH RISK</span>}
                                {ip.suspiciousScore > 0 && ip.suspiciousScore <= 4 && <span style={{ padding: "2px 6px", background: "#fde68a", color: "#d97706", borderRadius: "4px", fontSize: "9px", fontWeight: 600 }}>WATCH</span>}
                              </div>
                              <div style={{ fontSize: "11px", color: "#64748b", marginTop: 4 }}>
                                {ip.visits} visits • {ip.uniqueCountries} countries • {ip.uniqueSources} sources
                              </div>
                              {ip.daysBetween > 0 && (
                                <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: 2 }}>
                                  Active for {Math.round(ip.daysBetween)} days
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "20px", fontWeight: 700, color: ip.suspiciousScore > 4 ? "#dc2626" : ip.suspiciousScore > 0 ? "#d97706" : "#059669" }}>{ip.visits}</div>
                              <div style={{ fontSize: "10px", color: "#94a3b8" }}>score: {ip.suspiciousScore}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Repeat Visitors" subtitle="With loyalty indicators" badge={securityData?.repeatVisitors?.length} minHeight={380}>
                  {securityData?.repeatVisitors?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {securityData.repeatVisitors.slice(0, 8).map((visitor, index) => {
                        const maxVisits = Math.max(...securityData.repeatVisitors.map(v => v.visits), 1);
                        const widthPercent = (visitor.visits / maxVisits) * 100;
                        const loyaltyColors = { High: "#10b981", Medium: "#3b82f6", Low: "#f59e0b" };
                        const loyaltyBg = { High: "#ecfdf5", Medium: "#eff6ff", Low: "#fffbeb" };
                        return (
                          <div key={visitor.ip} style={{
                            padding: "12px 14px",
                            background: loyaltyBg[visitor.loyalty] || "#f8fafc",
                            borderRadius: "10px",
                            border: `1px solid ${visitor.loyalty === "High" ? "#a7f3d0" : visitor.loyalty === "Medium" ? "#93c5fd" : "#fde68a"}`
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <code style={{ fontSize: "12px", color: "#64748b" }}>{visitor.ip}</code>
                                <span style={{
                                  padding: "2px 6px",
                                  background: loyaltyColors[visitor.loyalty] || "#64748b",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  fontSize: "9px",
                                  fontWeight: 600
                                }}>{visitor.loyalty}</span>
                              </div>
                              <Text strong style={{ color: loyaltyColors[visitor.loyalty] || "#3b82f6" }}>{visitor.visits} visits</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{
                                width: `${widthPercent}%`,
                                height: "100%",
                                background: `linear-gradient(90deg, ${loyaltyColors[visitor.loyalty] || "#3b82f6"} 0%, ${loyaltyColors[visitor.loyalty] || "#3b82f6"}cc 100%)`,
                                borderRadius: "3px"
                              }} />
                            </div>
                            {visitor.timeSpanDays > 0 && (
                              <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: 4 }}>
                                Engaged for {Math.round(visitor.timeSpanDays)} days
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Country Anomalies */}
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <ChartCard title="Country Anomalies" subtitle="IPs appearing from multiple countries (possible VPN/proxy)" badge={securityData?.countryAnomalies?.length} minHeight={300}>
                  {securityData?.countryAnomalies?.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px" }}>
                      {securityData.countryAnomalies.slice(0, 12).map((anomaly, index) => (
                        <div key={anomaly.ip} style={{
                          padding: "14px",
                          background: anomaly.anomalyScore > 6 ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)" : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                          border: anomaly.anomalyScore > 6 ? "1px solid #fecaca" : "1px solid #fde68a",
                          borderRadius: "12px"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                            <code style={{ fontSize: "13px", fontWeight: 600 }}>{anomaly.ip}</code>
                            <span style={{
                              padding: "2px 8px",
                              background: anomaly.anomalyScore > 6 ? "#dc2626" : "#f59e0b",
                              color: "#fff",
                              borderRadius: "4px",
                              fontSize: "10px",
                              fontWeight: 600
                            }}>
                              {anomaly.countryCount} countries
                            </span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
                            {anomaly.countries?.slice(0, 5).map((country, i) => (
                              <span key={i} style={{
                                padding: "2px 6px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                                fontSize: "10px",
                                color: "#64748b"
                              }}>{country}</span>
                            ))}
                            {anomaly.countries?.length > 5 && (
                              <span style={{ fontSize: "10px", color: "#94a3b8" }}>+{anomaly.countries.length - 5} more</span>
                            )}
                          </div>
                          <div style={{ fontSize: "11px", color: "#64748b" }}>
                            {anomaly.visits} total visits • Anomaly score: {anomaly.anomalyScore}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <div style={{ fontSize: "48px", marginBottom: "12px" }}>✅</div>
                      <Text style={{ color: "#10b981", fontWeight: 600 }}>No country anomalies detected</Text>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>All IPs are consistent with single locations</div>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "geographic" && (
          <>
            {/* Summary Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={12} sm={6}>
                <div style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", borderRadius: "16px", padding: "20px", border: "1px solid #a7f3d0" }}>
                  <div style={{ fontSize: "12px", color: "#059669", fontWeight: 500, marginBottom: "4px" }}>Countries</div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#047857" }}>{geoData?.length || 0}</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", borderRadius: "16px", padding: "20px", border: "1px solid #93c5fd" }}>
                  <div style={{ fontSize: "12px", color: "#1d4ed8", fontWeight: 500, marginBottom: "4px" }}>EU Members</div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#1d4ed8" }}>{geoData?.filter(g => g.isEuMember)?.length || 0}</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ background: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)", borderRadius: "16px", padding: "20px", border: "1px solid #e879f9" }}>
                  <div style={{ fontSize: "12px", color: "#a855f7", fontWeight: 500, marginBottom: "4px" }}>Cities</div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#7c3aed" }}>{advancedGeoData?.regionAnalysis?.length || 0}</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", borderRadius: "16px", padding: "20px", border: "1px solid #fcd34d" }}>
                  <div style={{ fontSize: "12px", color: "#d97706", fontWeight: 500, marginBottom: "4px" }}>Timezones</div>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#b45309" }}>{[...new Set(geoData?.map(g => g.timezone).filter(Boolean))]?.length || 0}</div>
                </div>
              </Col>
            </Row>

            {/* Countries & Cities Row */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={12}>
                <ChartCard title="Top Countries" subtitle="With EU membership indicator" badge={geoData?.length} minHeight={400}>
                  {geoData?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {geoData.slice(0, 10).map((country, index) => {
                        const maxVisits = Math.max(...geoData.slice(0, 10).map(c => c.visits), 1);
                        const widthPercent = (country.visits / maxVisits) * 100;
                        return (
                          <div key={country.countryCode} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index < 3 ? "1px solid #99f6e4" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <span style={{ fontSize: "20px" }}>{getFlag(country.countryCode)}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                                  <Text style={{ fontWeight: 600, fontSize: "13px" }}>
                                    {index < 3 && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 4 }} />}
                                    {country.country}
                                  </Text>
                                  {country.isEuMember && (
                                    <span style={{ padding: "1px 6px", background: "#dbeafe", color: "#1d4ed8", borderRadius: "4px", fontSize: "9px", fontWeight: 600 }}>EU</span>
                                  )}
                                  {country.currencyCode && (
                                    <span style={{ fontSize: "10px", color: "#94a3b8" }}>{country.currencyCode}</span>
                                  )}
                                </div>
                                <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                                  <div style={{ width: `${widthPercent}%`, height: "100%", background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)", borderRadius: "3px" }} />
                                </div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: "16px", fontWeight: 700, color: "#7c3aed" }}>{country.visits.toLocaleString()}</div>
                                <div style={{ fontSize: "10px", color: "#64748b" }}>{country.uniqueCities} cities</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={12}>
                <ChartCard title="Top Cities" subtitle="Most active locations" badge={advancedGeoData?.regionAnalysis?.length} minHeight={400}>
                  {advancedGeoData?.regionAnalysis?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {advancedGeoData.regionAnalysis.slice(0, 10).map((region, index) => {
                        const maxVisits = Math.max(...advancedGeoData.regionAnalysis.slice(0, 10).map(r => r.visits), 1);
                        const widthPercent = (region.visits / maxVisits) * 100;
                        return (
                          <div key={`${region.city}-${region.country}`} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" : "#f8fafc",
                            border: index < 3 ? "1px solid #93c5fd" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                              <div>
                                <Text style={{ fontWeight: 600, fontSize: "13px" }}>
                                  {index < 3 && <EnvironmentOutlined style={{ color: "#3b82f6", marginRight: 4 }} />}
                                  {region.city || "Unknown"}
                                </Text>
                                <span style={{ color: "#94a3b8", fontSize: "12px", marginLeft: "6px" }}>{region.state}, {region.country}</span>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <span style={{ fontSize: "14px", fontWeight: 700, color: "#1d4ed8" }}>{region.visits}</span>
                              </div>
                            </div>
                            <div style={{ height: "5px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: "linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)", borderRadius: "3px" }} />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "10px", color: "#94a3b8" }}>
                              <span>{region.uniqueIPs} unique IPs</span>
                              <span>Density: {region.density?.toFixed(1) || 0}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* States & Coordinates Row */}
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Top States/Provinces" subtitle="Regional breakdown" minHeight={350}>
                  {advancedGeoData?.regionAnalysis?.length > 0 ? (() => {
                    // Group by state
                    const stateMap = {};
                    advancedGeoData.regionAnalysis.forEach(r => {
                      const key = `${r.state || 'Unknown'}, ${r.country}`;
                      if (!stateMap[key]) {
                        stateMap[key] = { state: r.state || 'Unknown', country: r.country, visits: 0, cities: 0 };
                      }
                      stateMap[key].visits += r.visits;
                      stateMap[key].cities += 1;
                    });
                    const states = Object.values(stateMap).sort((a, b) => b.visits - a.visits).slice(0, 10);
                    const maxVisits = Math.max(...states.map(s => s.visits), 1);
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {states.map((state, index) => {
                          const widthPercent = (state.visits / maxVisits) * 100;
                          return (
                            <div key={`${state.state}-${state.country}`} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{ width: "140px", fontSize: "12px", fontWeight: 500, color: "#64748b" }}>
                                {state.state}
                                <div style={{ fontSize: "10px", color: "#94a3b8" }}>{state.country}</div>
                              </div>
                              <div style={{ flex: 1, height: "20px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden", position: "relative" }}>
                                <div style={{
                                  width: `${widthPercent}%`,
                                  height: "100%",
                                  background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
                                  borderRadius: "4px"
                                }} />
                              </div>
                              <div style={{ width: "70px", textAlign: "right" }}>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: "#7c3aed" }}>{state.visits}</div>
                                <div style={{ fontSize: "9px", color: "#94a3b8" }}>{state.cities} cities</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })() : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={12}>
                <ChartCard title="Geographic Clusters" subtitle="Coordinate hotspots" minHeight={350}>
                  {advancedGeoData?.coordinateAnalysis?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {advancedGeoData.coordinateAnalysis.slice(0, 8).map((cluster, index) => (
                        <div key={index} style={{
                          padding: "14px",
                          background: index < 3 ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" : "#f8fafc",
                          border: index < 3 ? "1px solid #fcd34d" : "1px solid #e2e8f0",
                          borderRadius: "10px"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <Text strong style={{ color: index < 3 ? "#92400e" : "#1e293b" }}>
                                {index < 3 && <FireOutlined style={{ color: "#f59e0b", marginRight: 6 }} />}
                                Hotspot {index + 1}
                              </Text>
                              <div style={{ fontSize: "11px", color: "#94a3b8", fontFamily: "monospace", marginTop: "4px" }}>
                                [{cluster.coordinates?.[1]?.toFixed(2)}, {cluster.coordinates?.[0]?.toFixed(2)}]
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "18px", fontWeight: 700, color: index < 3 ? "#d97706" : "#8b5cf6" }}>{cluster.visits}</div>
                              <div style={{ fontSize: "10px", color: "#64748b" }}>
                                {cluster.countries?.length || 0} countries • {cluster.cities?.length || 0} cities
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "campaigns" && (
          <>
            {/* Source Type Summary */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              {(() => {
                const sourceTypes = {};
                campaignData?.sourceBreakdown?.forEach(s => {
                  sourceTypes[s.sourceType] = (sourceTypes[s.sourceType] || 0) + s.visits;
                });
                const typeEntries = Object.entries(sourceTypes).sort((a, b) => b[1] - a[1]);
                const typeColors = {
                  Production: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", border: "#a7f3d0", text: "#047857" },
                  Direct: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", border: "#93c5fd", text: "#1d4ed8" },
                  Campaign: { bg: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)", border: "#e879f9", text: "#7c3aed" },
                  Development: { bg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", border: "#fcd34d", text: "#b45309" },
                  "Netlify Deploy": { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", border: "#99f6e4", text: "#7c3aed" },
                  Other: { bg: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", border: "#cbd5e1", text: "#475569" },
                };
                return typeEntries.slice(0, 4).map(([type, visits]) => {
                  const colors = typeColors[type] || typeColors.Other;
                  return (
                    <Col xs={12} sm={6} key={type}>
                      <div style={{
                        background: colors.bg,
                        borderRadius: "16px",
                        padding: "20px",
                        border: `1px solid ${colors.border}`,
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: "28px", fontWeight: 700, color: colors.text }}>{visits.toLocaleString()}</div>
                        <div style={{ fontSize: "12px", color: colors.text, fontWeight: 500, marginTop: 4 }}>{type}</div>
                      </div>
                    </Col>
                  );
                });
              })()}
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={12}>
                <ChartCard title="Traffic Sources" subtitle="With source type and lifespan" badge={campaignData?.sourceBreakdown?.length} minHeight={450}>
                  {campaignData?.sourceBreakdown?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {campaignData.sourceBreakdown.slice(0, 10).map((source, index) => {
                        const typeColors = {
                          Production: { bg: "#ecfdf5", border: "#a7f3d0", badge: "#10b981" },
                          Direct: { bg: "#eff6ff", border: "#93c5fd", badge: "#3b82f6" },
                          Campaign: { bg: "#fdf4ff", border: "#e879f9", badge: "#a855f7" },
                          Development: { bg: "#fffbeb", border: "#fde68a", badge: "#f59e0b" },
                          "Netlify Deploy": { bg: "#f5f3ff", border: "#99f6e4", badge: "#8b5cf6" },
                          Other: { bg: "#f8fafc", border: "#e2e8f0", badge: "#64748b" },
                        };
                        const colors = typeColors[source.sourceType] || typeColors.Other;
                        return (
                          <div key={`${source.domain}-${index}`} style={{
                            padding: "14px 16px",
                            backgroundColor: colors.bg,
                            borderRadius: "12px",
                            border: `1px solid ${colors.border}`
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text strong style={{ fontSize: "13px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {index < 3 && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 4 }} />}
                                  {source.domain}
                                </Text>
                                <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                  <span style={{ padding: "2px 8px", background: colors.badge, color: "#fff", borderRadius: "4px", fontSize: "10px", fontWeight: 500 }}>{source.sourceType}</span>
                                  {source.lifespan > 0 && (
                                    <span style={{ padding: "2px 8px", background: "#f1f5f9", color: "#64748b", borderRadius: "4px", fontSize: "10px" }}>
                                      {Math.round(source.lifespan)}d active
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ textAlign: "right", marginLeft: "12px" }}>
                                <div style={{ fontSize: "20px", fontWeight: 700, color: colors.badge }}>{source.visits.toLocaleString()}</div>
                                <div style={{ fontSize: "10px", color: "#94a3b8" }}>{source.uniqueCountries} countries</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="UTM Campaigns" subtitle="Campaign performance with reach" badge={campaignData?.campaignExtraction?.length} minHeight={450}>
                  {campaignData?.campaignExtraction?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {campaignData.campaignExtraction.slice(0, 8).map((campaign, index) => {
                        const maxVisits = Math.max(...campaignData.campaignExtraction.map(c => c.visits), 1);
                        const widthPercent = (campaign.visits / maxVisits) * 100;
                        const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#06b6d4"];
                        const color = colors[index % colors.length];
                        return (
                          <div key={campaign.campaign} style={{
                            padding: "14px",
                            background: index < 3 ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" : "#fff",
                            border: `1px solid ${index < 3 ? "#e2e8f0" : "#f1f5f9"}`,
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {index < 3 && <TrophyOutlined style={{ color: "#f59e0b", fontSize: "12px" }} />}
                                <Text strong style={{ fontSize: "13px" }}>{campaign.campaign || "Unknown"}</Text>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "11px", color: "#94a3b8" }}>{campaign.uniqueCountries} countries</span>
                                <span style={{ fontSize: "16px", fontWeight: 700, color }}>{campaign.visits}</span>
                              </div>
                            </div>
                            <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{
                                width: `${widthPercent}%`,
                                height: "100%",
                                background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
                                borderRadius: "4px"
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <div style={{ fontSize: "48px", marginBottom: "12px" }}>📊</div>
                      <Text style={{ color: "#64748b", fontWeight: 600 }}>No UTM campaigns detected</Text>
                      <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>Add utm_source parameters to track campaigns</div>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Top Countries by Campaign */}
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <ChartCard title="Geographic Reach by Source" subtitle="Countries reached per traffic source" minHeight={200}>
                  {campaignData?.sourceBreakdown?.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                      {campaignData.sourceBreakdown.slice(0, 8).filter(s => s.countries?.length > 0).map((source, index) => (
                        <div key={`geo-${source.domain}-${index}`} style={{
                          padding: "12px 16px",
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          minWidth: "200px",
                          flex: "1 1 auto"
                        }}>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{source.domain}</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {source.countries?.slice(0, 6).map((country, i) => (
                              <span key={i} style={{
                                padding: "2px 6px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                                fontSize: "10px",
                                color: "#64748b"
                              }}>{country}</span>
                            ))}
                            {source.countries?.length > 6 && (
                              <span style={{ fontSize: "10px", color: "#94a3b8", padding: "2px 6px" }}>+{source.countries.length - 6}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "devices" && (
          <>
            {/* Device Type Cards with Percentages */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} sm={8}>
                <div style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #93c5fd",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={{ position: "absolute", right: "-10px", top: "-10px", fontSize: "80px", opacity: 0.1 }}>📱</div>
                  <div style={{ fontSize: "14px", color: "#1d4ed8", fontWeight: 500, marginBottom: "8px" }}>Mobile</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#1e40af" }}>
                    {(deviceData?.deviceTypeBreakdown?.find(d => d.deviceType === 'Mobile')?.visits || deviceData?.deviceTypes?.find(d => d.deviceType === 'mobile')?.visits || 0).toLocaleString()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: "rgba(59, 130, 246, 0.2)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: `${deviceData?.summary?.mobilePercentage || 0}%`, height: "100%", background: "#3b82f6", borderRadius: "3px" }} />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#1d4ed8" }}>{deviceData?.summary?.mobilePercentage || 0}%</span>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{
                  background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #a7f3d0",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={{ position: "absolute", right: "-10px", top: "-10px", fontSize: "80px", opacity: 0.1 }}>🖥️</div>
                  <div style={{ fontSize: "14px", color: "#059669", fontWeight: 500, marginBottom: "8px" }}>Desktop</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#047857" }}>
                    {(deviceData?.deviceTypeBreakdown?.find(d => d.deviceType === 'Desktop')?.visits || deviceData?.deviceTypes?.find(d => d.deviceType === 'desktop')?.visits || 0).toLocaleString()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: "rgba(16, 185, 129, 0.2)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: `${deviceData?.summary?.desktopPercentage || 0}%`, height: "100%", background: "#10b981", borderRadius: "3px" }} />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#059669" }}>{deviceData?.summary?.desktopPercentage || 0}%</span>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{
                  background: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e879f9",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={{ position: "absolute", right: "-10px", top: "-10px", fontSize: "80px", opacity: 0.1 }}>📋</div>
                  <div style={{ fontSize: "14px", color: "#a855f7", fontWeight: 500, marginBottom: "8px" }}>Tablet</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#7c3aed" }}>
                    {(deviceData?.deviceTypeBreakdown?.find(d => d.deviceType === 'Tablet')?.visits || deviceData?.deviceTypes?.find(d => d.deviceType === 'tablet')?.visits || 0).toLocaleString()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: "rgba(139, 92, 246, 0.2)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: `${deviceData?.summary?.tabletPercentage || 0}%`, height: "100%", background: "#8b5cf6", borderRadius: "3px" }} />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#a855f7" }}>{deviceData?.summary?.tabletPercentage || 0}%</span>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Summary Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24}>
                <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", justifyContent: "space-around" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>{deviceData?.summary?.totalVisitsAnalyzed?.toLocaleString() || 0}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>Total Analyzed</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#3b82f6" }}>{deviceData?.summary?.topBrowser || "—"}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>Top Browser</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#8b5cf6" }}>{deviceData?.summary?.topOS || "—"}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>Top OS</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#10b981" }}>{deviceData?.summary?.uniqueBrowsers || 0}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>Unique Browsers</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#f59e0b" }}>{deviceData?.summary?.uniqueOperatingSystems || 0}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>Unique OS</div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Browser Distribution" subtitle="With usage percentage" minHeight={400}>
                  {(deviceData?.browserBreakdown || deviceData?.browsers)?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {(deviceData.browserBreakdown || deviceData.browsers).slice(0, 10).map((browser, index) => {
                        const browsers = deviceData.browserBreakdown || deviceData.browsers;
                        const maxVisits = Math.max(...browsers.map(b => b.visits), 1);
                        const widthPercent = (browser.visits / maxVisits) * 100;
                        const colors = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f97316"];
                        const color = colors[index % colors.length];
                        return (
                          <div key={browser.browser} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" : "#fff",
                            border: index < 3 ? "1px solid #e2e8f0" : "1px solid #f1f5f9",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: color }} />
                                <Text strong style={{ fontSize: "13px" }}>{browser.browser || 'Unknown'}</Text>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{browser.percentage}%</span>
                                <span style={{ fontSize: "14px", fontWeight: 700, color }}>{browser.visits?.toLocaleString()}</span>
                              </div>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="OS Distribution" subtitle="With usage percentage" minHeight={400}>
                  {(deviceData?.osBreakdown || deviceData?.operatingSystems)?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {(deviceData.osBreakdown || deviceData.operatingSystems).slice(0, 10).map((os, index) => {
                        const osData = deviceData.osBreakdown || deviceData.operatingSystems;
                        const maxVisits = Math.max(...osData.map(o => o.visits), 1);
                        const widthPercent = (os.visits / maxVisits) * 100;
                        const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#06b6d4", "#84cc16", "#f97316"];
                        const color = colors[index % colors.length];
                        return (
                          <div key={os.os} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" : "#fff",
                            border: index < 3 ? "1px solid #e2e8f0" : "1px solid #f1f5f9",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: color }} />
                                <Text strong style={{ fontSize: "13px" }}>{os.os || 'Unknown'}</Text>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{os.percentage}%</span>
                                <span style={{ fontSize: "14px", fontWeight: 700, color }}>{os.visits?.toLocaleString()}</span>
                              </div>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default VisitorAnalytics;
